import { Vector } from "../../common/entities/Core/Vector";
import { ContentType, MindNode } from './MindNode';
import { Container, ContainerEvents } from "../../common/entities/Dom/Container";
import { Element, ElementEvent } from "../../common/entities/Dom/Element";
import { BentLine } from "../../common/entities/Shapes/BentLine";
import { Line } from "../../common/entities/Shapes/Line";

import { EventEmitter } from "../../common/entities/Core/EventEmitter";
import { RenderManager } from "../../common/entities/Core/RenderManager";
import { DraggableManager, DragEvents } from "../../common/entities/Abilities/Draggable/DraggableManager";
import { MouseButton, DragEventData } from "../../common/entities/Abilities/Draggable/BaseDraggable";
import { WheelManager, WheelEventData, WheelEvents } from "../../common/entities/Abilities/WheelManager";

import { layoutTree } from "../algorithm/LayoutRt";
// import { layoutTree } from "../algorithm/LayoutSelfDev";
import { AlignOptions } from "../algorithm/Alignment";

export { MindNode, MouseButton };

/**
 * 思维导图核心类
 * 提供思维导图的创建、布局计算、渲染和交互能力
 */
export class MindMap extends EventEmitter<MindMapEventMap> {

    private _rootNode: MindNode; // 拥有唯一根节点的思维导图节点集合
    private _outerContainer: Container; // 外部容器，不进行矩阵变换，仅包含 DOM 元素
    private _rootContainer: Container; // 根容器

    // 用于存储 Element 实例到 MindNode 的映射
    private _elementToMindNodeMap: Map<Element, MindNode> = new Map();
    // 用于存储子节点 ID 到其父节点连接线的映射
    private _linesMap: Map<string, Line> = new Map();
    // 用于存储 MindNode 到其原始数据对象（TreeNode 或 FlatNode）的映射
    private _mindNodeToOriginalDataMap: Map<MindNode, TreeNode | FlatNode> = new Map();

    private _isHorizontal: boolean; // 是否水平布局
    private _siblingSeparation: number; // 同级节点间距
    private _levelSeparation: number; // 层级间距
    private _initialOffset?: { x?: number; y?: number }; // 初始偏移量
    private _lineShape: 'straight' | 'curved' | 'zigzag'; // 线条形状
    private _debugOptions?: DebugOptions; // 调试选项
    private _isNativeFullscreen: boolean = false;   // 是否处于原生全屏状态
    private _fullscreenTransitioning: boolean = false; // 原生全屏状态切换是否正在进行中
    private _isPseudoFullscreen: boolean = false; // 是否处于模拟全屏状态
    private _originalBodyStyle?: { overflow: string }; // 保存模拟全屏前 body 的样式

    private _align: MindMapAlign; // 对齐选项
    private _style: MindMapStyle; // 样式
    private _fullscreenOptions: Required<FullscreenOptions>; // 存储全屏选项
    private _draggableManager?: DraggableManager; // 拖拽管理器
    private _dragOptions: Required<MindMapDragOptions>; // 存储拖拽配置
    private _wheelManager?: WheelManager; // 鼠标滚轮管理器
    private _zoomOptions: Required<MindMapZoomOptions>; // 存储缩放选项

    /** 存储构造函数传入的 contentCallback */
    private _nodeContentCallback?: (originalData: NodeLike) => string | HTMLElement | HTMLElement[];
    /** 全屏状态变化监听器 */
    private _fullscreenChangeListener?: () => void;

    // #region 构造与销毁

    /**
     * 创建思维导图实例
     * @param data - 思维导图初始数据，可以是 MindNode 实例、TreeNode 对象或 FlatNode 数组
     * @param containerDom - 承载思维导图的 DOM 容器元素
     * @param options - 思维导图配置选项
     * @param options.isHorizontal - 是否水平布局，默认为 true
     * @param options.siblingSeparation - 同级节点间距(边界到边界)，默认为 16
     * @param options.levelSeparation - 层级间的最小距离(边界到边界)，默认为 32
     * @param options.initialOffset - 初始偏移量，可选
     * @param options.lineShape - 连接线形状，可选值为 'straight'|'curved'|'zigzag'，默认为 'curved'
     * @param options.contentCallback - 节点显示内容回调函数
     * @param options.align - 思维导图对齐选项
     * @param options.style - 思维导图样式配置
     * @param options.fullscreenOptions - 全屏相关配置
     * @param options.dragOptions - 拖拽相关配置
     * @param options.zoomOptions - 缩放相关配置
     * @param options.debugOptions - 调试选项
     */
    constructor(
        data: MindNode | TreeNode | FlatNode[],
        containerDom: HTMLElement,
        options: {
            isHorizontal?: boolean;
            siblingSeparation?: number;
            levelSeparation?: number;
            initialOffset?: { x?: number; y?: number };
            lineShape?: 'straight' | 'curved' | 'zigzag';
            contentCallback?: (originalData: NodeLike) => string | HTMLElement | HTMLElement[];
            align?: MindMapAlign;
            style?: MindMapStyle;
            fullscreenOptions?: FullscreenOptions;
            dragOptions?: MindMapDragOptions;
            zoomOptions?: MindMapZoomOptions;
            debugOptions?: DebugOptions; // 调试选项
        } = {}
    ) {
        super();
        const {
            isHorizontal = true,
            siblingSeparation = 16,
            levelSeparation = 32,
            initialOffset,
            lineShape = 'curved',
            contentCallback,
            align = { mode: 'cross-axis-start-edge' }, // 默认对齐模式
            style,
            fullscreenOptions = {},
            dragOptions = {},
            zoomOptions = {},
            debugOptions = {}, // 调试选项
        } = options;

        this._isHorizontal = isHorizontal;
        this._siblingSeparation = siblingSeparation;
        this._levelSeparation = levelSeparation;
        this._initialOffset = initialOffset;
        this._lineShape = lineShape;
        this._align = align;
        this._nodeContentCallback = contentCallback;

        // 设置样式
        const defaultStyle: Required<MindMapStyle> = {
            lineWidth: 1,
            lineColor: '#000000',
            nodeBorderWidth: 1,
            nodeBgColor: '#FFFFFF',
            nodeBorderColor: '#000000',
        }
        this._style = { ...defaultStyle, ...style };

        // 设置全屏选项
        this._fullscreenOptions = { ...DEFAULT_FULLSCREEN_OPTIONS, ...fullscreenOptions };

        // 设置拖拽选项
        this._dragOptions = { ...DEFAULT_DRAG_OPTIONS, ...dragOptions };

        // 设置缩放选项
        this._zoomOptions = { ...DEFAULT_ZOOM_OPTIONS, ...zoomOptions };

        // 设置调试选项
        this._debugOptions = debugOptions;

        // 将外部对象转换为 MindNode
        this._rootNode = data instanceof MindNode
            ? data
            : Array.isArray(data)
                ? this._createMindNodesFromFlatData(data)
                : this._createMindNodesFromTreeData(data);

        this._outerContainer = new Container({ dom: containerDom });
        this._rootContainer = new Container({
            overflowable: true,
            zoomFactorBase: 1 + this._zoomOptions.zoomStep // zoomStep 转换为 zoomFactorBase
        });

        // 监听外部容器的尺寸变化事件
        this._setupContainerResizeListener();

        // 计算布局并渲染
        this._layoutAndCreateElementsAndRender();

        // 初始化拖拽管理器
        this._initializeDraggableManager();

        // 初始化鼠标滚轮管理器
        this._initializeWheelManager();

        // 初始化全屏监听器
        this._initializeFullscreenListener();
    }

    /**
     * 销毁思维导图实例，释放所有相关资源。
     * 调用此方法后，MindMap 实例将不可用。
     */
    destroy() {
        // 清理全屏监听器
        if (this._fullscreenChangeListener) {
            document.removeEventListener('fullscreenchange', this._fullscreenChangeListener);
            this._fullscreenChangeListener = undefined;
        }
        // 如果当前处于全屏状态，先退出全屏
        // 直接检查 document.fullscreenElement 以确保与真实状态同步，而不是依赖 _isNativeFullscreen，更加可靠
        if (document.fullscreenElement === this._outerContainer?.target?.htmlEle) {
            document.exitFullscreen().catch(console.error);
        }

        // 1. 销毁拖拽管理器
        this._draggableManager?.dispose();
        this._draggableManager = undefined;
        // 2. 销毁滚轮缩放管理器
        this._wheelManager?.dispose();
        this._wheelManager = undefined;
        // 3. 销毁容器(及容器内的所有子元素)
        this._outerContainer.dispose();
        // 4. 清空内部映射表
        this._elementToMindNodeMap.clear();
        this._linesMap.clear();
        this._mindNodeToOriginalDataMap.clear(); // 清空原始数据映射
        // 5. 将主要属性置为 undefined，以帮助垃圾回收并明确表示实例已销毁
        this._rootNode = undefined as any; // 使用 undefined 代替 MindNode 类型
        (this._outerContainer as any) = undefined;
        (this._rootContainer as any) = undefined;

        this.emit(MindMapEvents.MINDMAP_DESTROYED, undefined);
    }

    /**
     * 清空思维导图实例的所有内容和状态
     * 包括清除容器、映射以及释放事件监听器
     * @todo 考虑是否设置为私有
     */
    clear() {
        this._outerContainer.clear();
        this._rootContainer.clear(); // 确保 rootContainer 也被清空
        this._draggableManager?.dispose();
        this._draggableManager = undefined;
        this._wheelManager?.dispose();
        this._wheelManager = undefined;
        this._elementToMindNodeMap.clear(); // 清空映射
        this._linesMap.clear(); // 清空映射
        this._mindNodeToOriginalDataMap.clear(); // 清空原始数据映射
    }

    // #endregion 构造与销毁

    // #region 公共属性访问器

    /**
     * 获取思维导图当前布局方向
     * @returns 是否为水平布局
     */
    get isHorizontal(): boolean { return this._isHorizontal; }

    /**
     * 获取当前是否处于全屏状态 (包括原生或模拟)
     * @returns 是否处于全屏状态
     */
    get isFullscreen(): boolean {
        return this.isNativeFullscreen || this.isPseudoFullscreen;
    }

    /**
     * 获取当前是否处于原生全屏状态
     * @returns 是否处于原生全屏状态
     */
    get isNativeFullscreen(): boolean {
        return this._isNativeFullscreen;
    }

    /**
     * 获取当前是否处于模拟全屏状态
     * @returns 是否处于模拟全屏状态
     */
    get isPseudoFullscreen(): boolean {
        return this._isPseudoFullscreen;
    }

    // #endregion 公共属性访问器

    // #region 数据管理

    /**
     * 设置新的思维导图数据
     * 这将会清空当前的思维导图，并使用新的数据重新构建和渲染
     * @param data - 新的思维导图数据，可以是 MindNode 实例、TreeNode 对象或 FlatNode 数组
     */
    setRootNodeData(data: MindNode | TreeNode | FlatNode[]): void {
        // 1. 彻底清空现有状态
        // 这会处理掉现有的管理器、清空数据映射表，并从 DOM 中移除旧的节点
        this.clear();

        // 2. 处理新数据并设置新的 rootNode
        // _createMindNodesFromTreeData 和 _createMindNodesFromFlatData 方法
        // 会负责填充 this._mindNodeToOriginalDataMap
        this._rootNode = data instanceof MindNode
            ? data
            : Array.isArray(data)
                ? this._createMindNodesFromFlatData(data)
                : this._createMindNodesFromTreeData(data);

        // 3. 重建思维导图
        // rebuild 方法会调用 _layoutAndCreateElementsAndRender (进行布局计算和渲染)
        // 以及 _initializeDraggableManager (为新节点设置拖拽)
        this.rebuild();

        // // 4. 重新初始化滚轮管理器，因为 clear() 方法会将其销毁
        // this._initializeWheelManager();
    }

    /**
     * 从外部树形对象创建 MindNode 树结构
     * @param data - 树形节点数据
     * @returns 构建的根 MindNode 实例
     */
    private _createMindNodesFromTreeData(data: TreeNode): MindNode {
        // 处理位置
        let position: Vector | undefined;
        if (data.position) {
            position = data.position instanceof Vector
                ? data.position.clone() // 克隆以避免共享引用
                : new Vector(data.position.x, data.position.y);
        }

        // 处理尺寸
        let size: Vector | undefined;
        if (data.size) {
            size = data.size instanceof Vector
                ? data.size.clone() // 克隆以避免共享引用
                : new Vector(data.size.x, data.size.y);
        }

        // 创建节点
        const node = new MindNode({
            id: data.id, // 确保 ID 存在且唯一
            position,
            size,
            collapsed: data.collapsed,
        });

        // 存储 MindNode 与原始 TreeNode 的映射
        this._mindNodeToOriginalDataMap.set(node, data);

        // 设置内容
        if (data.content !== undefined) {
            node.content = data.content as ContentType; // 确保 content 是 ContentType
        }

        // 递归处理子节点
        if (data.children && Array.isArray(data.children)) {
            data.children.forEach(childData => {
                const childNode = this._createMindNodesFromTreeData(childData);
                node.addChild(childNode); // addChild 会自动设置 parent
            });
        }

        return node;
    }

    /**
     * 从扁平数据数组创建 MindNode 树结构
     * @param flatNodes - 扁平结构的节点数组
     * @returns 构建的根 MindNode 实例
     */
    private _createMindNodesFromFlatData(flatNodes: FlatNode[]): MindNode {
        if (!flatNodes || flatNodes.length === 0) {
            throw new Error('扁平节点数组不能为空');
        }

        // 创建节点映射表以便快速查找
        const nodeMap = new Map<string, MindNode>();
        // 用于生成唯一ID的计数器
        let autoIdCounter = 0;

        // 第一步：创建所有 MindNode 实例
        flatNodes.forEach(flatNode => {
            // 自动为没有ID的节点生成唯一ID
            const nodeId = flatNode.id || `auto_id_${autoIdCounter++}`;

            // 处理位置
            let position: Vector | undefined;
            if (flatNode.position) {
                position = flatNode.position instanceof Vector
                    ? flatNode.position.clone()
                    : new Vector(flatNode.position.x, flatNode.position.y);
            }

            // 处理尺寸
            let size: Vector | undefined;
            if (flatNode.size) {
                size = flatNode.size instanceof Vector
                    ? flatNode.size.clone()
                    : new Vector(flatNode.size.x, flatNode.size.y);
            }

            // 创建节点
            const node = new MindNode({
                id: nodeId,
                position,
                size,
                collapsed: flatNode.collapsed,
            });

            // 存储 MindNode 与原始 FlatNode 的映射
            this._mindNodeToOriginalDataMap.set(node, flatNode);

            // 设置内容
            if (flatNode.content !== undefined) {
                node.content = flatNode.content as ContentType; // 确保 content 是 ContentType
            }

            // 存储创建的节点和原始节点ID的映射关系
            nodeMap.set(nodeId, node);

            // 如果自动生成了ID，将它赋回原始对象，便于后续处理
            if (!flatNode.id) {
                flatNode.id = nodeId;
            }
        });

        // 第二步：建立节点之间的父子关系
        let rootNode: MindNode | undefined;

        flatNodes.forEach(flatNode => {
            const nodeId = flatNode.id;
            if (!nodeId) return; // 安全检查，理论上不会发生

            const currentNode = nodeMap.get(nodeId);
            if (!currentNode) return; // 安全检查，理论上不会发生

            if (flatNode.parentId) {
                // 如果有父节点，则添加为子节点
                const parentNode = nodeMap.get(flatNode.parentId);
                if (!parentNode) {
                    console.warn(`找不到 ID 为 ${flatNode.parentId} 的父节点，节点 ${nodeId} 将被视为根节点`);
                    if (!rootNode) rootNode = currentNode;
                } else {
                    parentNode.addChild(currentNode);
                }
            } else {
                // 没有父节点的是根节点
                if (rootNode) {
                    console.warn('发现多个根节点，将使用第一个发现的作为根节点');
                } else {
                    rootNode = currentNode;
                }
            }
        });

        // 如果没有找到根节点，选择第一个节点作为根节点
        if (!rootNode && nodeMap.size > 0) {
            rootNode = nodeMap.values().next().value;
            console.warn('未找到明确的根节点，使用第一个节点作为根节点');
        }

        if (!rootNode) {
            throw new Error('无法创建思维导图：没有可用节点');
        }

        return rootNode;
    }

    /**
     * 获取与给定 MindNode 关联的原始数据对象 (TreeNode 或 FlatNode)
     * 如果 MindMap 是直接用 MindNode 实例初始化的，则返回该 MindNode 实例本身
     * @param node - 要获取原始数据的 MindNode 实例
     * @returns 关联的原始数据对象，或者 MindNode 实例本身
     */
    private _getOriginalDataForNode(node: MindNode): NodeLike {
        return this._mindNodeToOriginalDataMap.get(node) || node;
    }

    // #endregion 数据管理

    // #region 节点查找与操作

    /**
     * 根据 ID 查找 MindNode
     * @param nodeId - 要查找的节点 ID
     * @returns 找到的 MindNode，如果未找到则返回 undefined
     */
    findNodeById(nodeId: string): MindNode | undefined {
        if (!this._rootNode) {
            return undefined;
        }
        return this._findNodeByIdRecursive(this._rootNode, nodeId);
    }

    /**
     * 递归查找具有指定 ID 的 MindNode
     * @param node - 当前开始搜索的节点
     * @param nodeId - 要查找的节点 ID
     * @returns 找到的 MindNode，如果未找到则返回 undefined
     */
    private _findNodeByIdRecursive(node: MindNode, nodeId: string): MindNode | undefined {
        if (node.id === nodeId) {
            return node;
        }
        for (const child of node.children) {
            const found = this._findNodeByIdRecursive(child, nodeId);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    /**
     * 设置指定节点的尺寸
     * @param nodeId - 要设置尺寸的节点 ID
     * @param size - 新的尺寸配置，可以只设置宽度或高度
     * @param size.width - 新的宽度，可选
     * @param size.height - 新的高度，可选
     * @returns 如果节点找到并成功设置尺寸，则返回 true；否则返回 false
     */
    setNodeSize(nodeId: string, size: { width?: number, height?: number }): boolean {
        const node = this.findNodeById(nodeId);
        if (!node) {
            console.warn(`[MindMap] 无法设置节点尺寸：未找到 ID 为 '${nodeId}' 的节点`);
            return false;
        }

        // 检查是否至少提供了一个有效的尺寸值
        if (size.width === undefined && size.height === undefined) {
            console.warn(`[MindMap] 无法设置节点尺寸：未提供有效的宽度或高度值`);
            return false;
        }

        // 记录原始尺寸以便计算变化
        const originalSize = { x: node.size.x, y: node.size.y };

        // 更新 MindNode 的尺寸，只更新提供的值
        if (size.width !== undefined) {
            node.size.x = size.width;
        }
        if (size.height !== undefined) {
            node.size.y = size.height;
        }

        // 查找对应的 Element 并更新其尺寸
        let targetElement: Element | undefined;
        for (const [element, mindNode] of this._elementToMindNodeMap.entries()) {
            if (mindNode === node) {
                targetElement = element;
                element.setSize({
                    width: node.size.x,
                    height: node.size.y
                });
                break;
            }
        }

        // 检查尺寸是否真的发生了变化
        const sizeChanged = originalSize.x !== node.size.x || originalSize.y !== node.size.y;

        if (sizeChanged) {
            // 重新计算布局并更新受影响的节点位置
            this._updateLayoutForNodeSizeChange(node);

            // 更新所有连接线
            this._updateAllConnectionLines();

            // 重新计算容器边界
            this._updateContainerBounds();
        }

        return true;
    }

    /**
     * 更新指定节点的内容
     * 此方法会根据传入的节点 ID 数组，并根据节点自身的 `content` 属性或外部传入的 `contentCallback`
     * 来重新解析和设置节点内部的内容。
     * @param nodeIds - 要更新内容的节点 ID 数组，不传参时默认更新所有节点
     * @returns 成功更新的节点个数
     */
    updateNodeContents(nodeIds?: string[]): number {
        if (!this._rootNode || this._elementToMindNodeMap.size === 0) {
            return 0;
        }

        let successCount = 0;

        if (!nodeIds) {
            // 不传参时更新所有节点
            for (const [element, mindNode] of this._elementToMindNodeMap.entries()) {
                const newResolvedContent = this._resolveNodeContent(mindNode);
                if (newResolvedContent !== undefined) {
                    element.setContent(newResolvedContent);
                    successCount++;
                }
            }
        } else {
            // 传参时更新指定节点
            for (const nodeId of nodeIds) {
                const mindNode = this.findNodeById(nodeId);
                if (!mindNode) {
                    console.warn(`[MindMap] 无法更新节点内容：未找到 ID 为 '${nodeId}' 的节点`);
                    continue;
                }

                // 查找对应的 Element
                let elementToUpdate: Element | undefined;
                for (const [element, mappedNode] of this._elementToMindNodeMap.entries()) {
                    if (mappedNode === mindNode) {
                        elementToUpdate = element;
                        break;
                    }
                }

                if (!elementToUpdate) {
                    console.warn(`[MindMap] 无法更新节点内容：未找到节点 '${nodeId}' 对应的 Element`);
                    continue;
                }

                const newResolvedContent = this._resolveNodeContent(mindNode);
                if (newResolvedContent !== undefined) {
                    elementToUpdate.setContent(newResolvedContent);
                    successCount++;
                }
            }
        }

        return successCount;
    }

    // #endregion 节点查找与操作

    // #region 节点折叠管理

    /**
     * 切换节点的折叠状态
     * @param nodeId - 要切换折叠状态的节点 ID
     * @returns 如果节点找到并成功切换状态，则返回 true；否则返回 false
     */
    toggleNodeCollapse(nodeId: string): boolean {
        const node = this.findNodeById(nodeId);
        if (!node) {
            console.warn(`[MindMap] 无法切换折叠状态：未找到 ID 为 '${nodeId}' 的节点`);
            return false;
        }

        // const oldCollapsed = node.collapsed;
        node.collapsed = !node.collapsed;

        // 重新计算布局并渲染
        this.rebuild();

        // 触发节点折叠状态变更事件
        this.emit(MindMapEvents.NODE_COLLAPSE_CHANGED, {
            nodeId,
            collapsed: node.collapsed,
            node: this._getOriginalDataForNode(node)
        } as MindMapNodeCollapseEvent);

        return true;
    }

    /**
     * 设置节点的折叠状态
     * @param nodeId - 要设置折叠状态的节点 ID
     * @param collapsed - 要设置的折叠状态
     * @returns 如果节点找到并成功设置状态，则返回 true；否则返回 false
     */
    setNodeCollapsed(nodeId: string, collapsed: boolean): boolean {
        const node = this.findNodeById(nodeId);
        if (!node) {
            console.warn(`[MindMap] 无法设置折叠状态：未找到 ID 为 '${nodeId}' 的节点`);
            return false;
        }

        const oldCollapsed = node.collapsed;

        // 如果状态没有变化，直接返回 true
        if (oldCollapsed === collapsed) {
            return true;
        }

        node.collapsed = collapsed;

        // 重新计算布局并渲染
        this.rebuild();

        // 触发节点折叠状态变更事件
        this.emit(MindMapEvents.NODE_COLLAPSE_CHANGED, {
            nodeId,
            collapsed: node.collapsed,
            node: this._getOriginalDataForNode(node)
        } as MindMapNodeCollapseEvent);

        return true;
    }

    /**
     * 获取节点的折叠状态
     * @param nodeId - 要查询的节点 ID
     * @returns 节点的折叠状态，如果节点不存在则返回 undefined
     */
    getNodeCollapsed(nodeId: string): boolean | undefined {
        const node = this.findNodeById(nodeId);
        return node ? node.collapsed : undefined;
    }

    /**
     * 展开所有节点
     * @returns 成功展开的节点数量
     */
    expandAllNodes(): number {
        let expandedCount = 0;

        const expandNodeRecursively = (node: MindNode): void => {
            if (node.collapsed) {
                node.collapsed = false;
                expandedCount++;

                // 触发折叠状态变更事件
                this.emit(MindMapEvents.NODE_COLLAPSE_CHANGED, {
                    nodeId: node.id,
                    collapsed: false,
                    node: this._getOriginalDataForNode(node)
                } as MindMapNodeCollapseEvent);
            }

            // 递归处理子节点
            node.children.forEach(child => expandNodeRecursively(child));
        };

        expandNodeRecursively(this._rootNode);

        if (expandedCount > 0) {
            // 重新计算布局并渲染
            this.rebuild();
        }

        return expandedCount;
    }

    /**
     * 折叠所有非根节点
     * @returns 成功折叠的节点数量
     */
    collapseAllNodes(): number {
        let collapsedCount = 0;

        const collapseNodeRecursively = (node: MindNode, isRoot: boolean = false): void => {
            // 不折叠根节点
            if (!isRoot && !node.collapsed) {
                node.collapsed = true;
                collapsedCount++;

                // 触发折叠状态变更事件
                this.emit(MindMapEvents.NODE_COLLAPSE_CHANGED, {
                    nodeId: node.id,
                    collapsed: true,
                    node: this._getOriginalDataForNode(node)
                } as MindMapNodeCollapseEvent);
            }

            // 递归处理子节点
            node.children.forEach(child => collapseNodeRecursively(child, false));
        };

        collapseNodeRecursively(this._rootNode, true);

        if (collapsedCount > 0) {
            // 重新计算布局并渲染
            this.rebuild();
        }

        return collapsedCount;
    }

    // #endregion 节点折叠管理

    // #region 视图控制

    /**
     * 设置思维导图布局方向
     * @param isHorizontal - 是否水平布局
     */
    setHorizontal(isHorizontal: boolean) {
        if (this._isHorizontal !== isHorizontal) {
            this._isHorizontal = isHorizontal;
            this.rebuild();
        }
    }

    /**
     * 将指定 ID 的节点聚焦到视口中心
     * @param nodeId - 要聚焦的节点的 ID
     * @returns 如果节点找到并成功聚焦，则返回 true；否则返回 false
     */
    focusOnNode(nodeId: string): boolean {
        const targetNode = this.findNodeById(nodeId);

        if (!targetNode) {
            console.warn(`[MindMap] 无法聚焦：未找到 ID 为 '${nodeId}' 的节点`);
            return false;
        }

        // 1. 计算目标节点在 rootContainer 坐标系中的中心点
        const nodeLocalCenterX = targetNode.position.x + targetNode.size.x / 2;
        const nodeLocalCenterY = targetNode.position.y + targetNode.size.y / 2;

        // 2. 获取视口 (outerContainer) 的中心点
        const viewportCenterX = this._outerContainer.width / 2;
        const viewportCenterY = this._outerContainer.height / 2;

        // 3. 获取 rootContainer 当前的缩放比例
        const currentScaleX = this._rootContainer.scaling.x;
        const currentScaleY = this._rootContainer.scaling.y;

        // 4. 计算 rootContainer 需要移动到的新位置，
        //    使得目标节点的中心点在缩放后与视口中心对齐。
        //    公式: viewportCenter = newRootContainerPos + nodeLocalCenter * scale
        //    因此: newRootContainerPos = viewportCenter - nodeLocalCenter * scale
        const newRootContainerX = viewportCenterX - (nodeLocalCenterX * currentScaleX);
        const newRootContainerY = viewportCenterY - (nodeLocalCenterY * currentScaleY);

        // 5. 移动 rootContainer 到计算出的新位置
        this._rootContainer.moveTo(newRootContainerX, newRootContainerY);

        // 移动操作通常会触发重绘，如果未自动重绘，可能需要手动调用渲染
        // RenderManager.getInstance().renderContainer(this._outerContainer);

        return true;
    }

    // #endregion 视图控制

    // #region 缩放控制

    /**
     * 放大思维导图
     * @param cx - 缩放中心点的 X 坐标，默认为 0
     * @param cy - 缩放中心点的 Y 坐标，默认为 0
     */
    zoomIn(cx: number = 0, cy: number = 0): void {
        this._zoomIn(cx, cy);
    }

    /**
     * 缩小思维导图
     * @param cx - 缩放中心点的 X 坐标，默认为 0
     * @param cy - 缩放中心点的 Y 坐标，默认为 0
     */
    zoomOut(cx: number = 0, cy: number = 0): void {
        this._zoomOut(cx, cy);
    }

    /**
     * 重置思维导图缩放比例至默认值
     * @param cx - 缩放中心点的 X 坐标，默认为 0
     * @param cy - 缩放中心点的 Y 坐标，默认为 0
     */
    resetZoom(cx: number = 0, cy: number = 0) {
        this._rootContainer.resetZoom(cx, cy);
        this.emit(MindMapEvents.ZOOM_CHANGE, {
            scale: this._rootContainer.scaling,
            mouseX: cx,
            mouseY: cy,
        } as MindMapZoomEvent);
    }


    /**
     * 内部放大方法，支持传入原始事件
     * @param cx - 缩放中心点的 X 坐标
     * @param cy - 缩放中心点的 Y 坐标
     * @param originalEvent - 原始滚轮事件，可选
     */
    private _zoomIn(cx: number, cy: number, originalEvent?: WheelEvent): void {
        this._rootContainer.zoomIn(cx, cy);
        this.emit(MindMapEvents.ZOOM_CHANGE, {
            originalEvent,
            scale: this._rootContainer.scaling,
            mouseX: cx,
            mouseY: cy,
        } as MindMapZoomEvent);
    }

    /**
     * 内部缩小方法，支持传入原始事件
     * @param cx - 缩放中心点的 X 坐标
     * @param cy - 缩放中心点的 Y 坐标
     * @param originalEvent - 原始滚轮事件，可选
     */
    private _zoomOut(cx: number, cy: number, originalEvent?: WheelEvent): void {
        this._rootContainer.zoomOut(cx, cy);
        this.emit(MindMapEvents.ZOOM_CHANGE, {
            originalEvent,
            scale: this._rootContainer.scaling,
            mouseX: cx,
            mouseY: cy,
        } as MindMapZoomEvent);
    }

    // #endregion 缩放控制

    // #region 布局计算

    /**
     * 重置思维导图布局并重新渲染
     * @todo 考虑是否设置为私有
     */
    rebuild() {
        this._layoutAndCreateElementsAndRender();
        this._initializeDraggableManager();
        this._initializeWheelManager();
    }

    /**
     * 计算思维导图布局
     * @returns 布局计算后的根节点
     */
    private _calculateLayoutInternal(): MindNode {
        const { width: containerWidth, height: containerHeight } = this._outerContainer;
        const initialOffsetVector = this._initialOffset
            ? new Vector(this._initialOffset.x ?? 0, this._initialOffset.y ?? 0)
            : undefined;

        // return new LayoutAlgo().calculateLayout(this.node, {
        //     isHorizontal,
        //     nodeGap: siblingSeparation,
        //     levelGap: levelSeparation,
        //     initialOffset
        // }) as MindNode;

        const align = {
            mode: this._align.mode,
            viewport: { x: 0, y: 0, width: containerWidth, height: containerHeight },
            targetNodeId: this._align.targetNodeId,
        }

        return layoutTree(this._rootNode, {
            isHorizontal: this._isHorizontal,
            siblingSeparation: this._siblingSeparation,
            levelSeparation: this._levelSeparation,
            offset: initialOffsetVector,
            align,
            layered: true, // 是否分层布局
        }) as MindNode;
    }

    /**
     * 当节点尺寸变化时，重新计算布局并更新受影响的节点位置
     * @param changedNode - 尺寸发生变化的节点
     */
    private _updateLayoutForNodeSizeChange(changedNode: MindNode): void {
        // 重新计算整个树的布局
        this._calculateLayoutInternal();

        // 更新所有节点的 Element 位置
        this._updateAllElementPositions();
    }

    // #endregion 布局计算

    // #region 渲染与元素创建

    /**
     * 执行布局计算、创建元素和连接线，添加元素到容器并渲染思维导图
     */
    private _layoutAndCreateElementsAndRender(): void {
        this._calculateLayoutInternal();

        // 首先清除 rootContainer 的子元素
        // 这个操作会触发容器内所有旧 Element 实例的 dispose() 方法，
        // 进而触发 ElementEvent.DISPOSE 事件。
        // 此时，this.elementToMindNodeMap 尚未清空，
        // 因此 ElementEvent.DISPOSE 的监听器可以正确查找到 MindNode 并发出 MindMapEvents.NODE_DESTROYED
        this._rootContainer.clear();  // 清除 rootContainer 的子元素

        // 在旧元素被销毁并触发了相关事件后，再清空映射表，为新元素的创建做准备
        this._elementToMindNodeMap.clear(); // 清空旧的映射
        this._linesMap.clear();       // 清空旧的映射

        // 使用递归辅助函数填充映射并获取元素和线条
        const [elements, lines] = this._mindNodeToElementsAndLinesRecursive(this._rootNode, this._isHorizontal);

        // 添加元素到容器
        elements.forEach(element => this._rootContainer.addChild(element));
        lines.forEach(line => this._rootContainer.addChild(line));

        // 应用调试选项：根容器背景颜色
        if (this._debugOptions?.rootContainerBgColor) {
            this._rootContainer.applyBackground({
                backgroundColor: this._debugOptions.rootContainerBgColor,
                // opacity: 0.5, // 设置透明度以便调试
            });
        }

        const bbox = this._rootContainer.getLocalBoundingBox(); // 计算边界框
        // console.log('bbox :>> ', bbox);

        this._rootContainer.setSize({
            width: bbox.width,
            height: bbox.height,
        });

        this._outerContainer.addChild(this._rootContainer);

        // 使用渲染管理器进行渲染
        RenderManager.getInstance().renderContainer(this._outerContainer);

        if (this._debugOptions?.drawGridBackground) {
            // 绘制背景网格
            this._outerContainer.drawGridBackground(); // 可以在 render 后调用，或者根据需要调整
        }

        this.emit(MindMapEvents.LAYOUT_UPDATED, {
            direction: this._isHorizontal ? 'horizontal' : 'vertical',
        } as MindMapLayoutEvent);
    }

    /**
     * 将 MindNode 转换为 Element 和 Line 数组
     * @param node - 要转换的思维导图节点
     * @param isHorizontal - 是否为水平布局
     * @returns 包含元素和连接线的元组
     */
    private _mindNodeToElementsAndLinesRecursive(
        node: MindNode,
        isHorizontal: boolean
    ): [Element[], Line[]] {
        const elements: Element[] = [];
        const lines: Line[] = [];

        const element = this._createNodeElement(node); // _createNodeElement 内部会填充 elementToMindNodeMap
        elements.push(element);

        if (node.parent) {
            const line = this._createConnectionLine(node.parent, node, isHorizontal);
            lines.push(line);
            // 使用子节点的 ID 作为键，存储连接到该子节点的线
            if (node.id) { // 确保 node.id 存在
                this._linesMap.set(node.id, line);
            }
        }

        // 如果节点未折叠，则递归处理其子节点
        if (!node.collapsed) {
            node.children.forEach(child => {
                const [childElements, childLines] = this._mindNodeToElementsAndLinesRecursive(child, isHorizontal);
                elements.push(...childElements);
                lines.push(...childLines);
            });
        }

        return [elements, lines];
    }

    /**
     * 创建节点元素
     * @param node - 节点对象
     * @returns 节点元素对象
     */
    private _createNodeElement(node: MindNode): Element {
        const element = new Element({ id: node.id });
        element.setSize({ width: node.size.x, height: node.size.y });
        element.moveTo(node.position.x, node.position.y);

        const { nodeBgColor, nodeBorderColor, nodeBorderWidth } = this._style;
        element.applyBorder({ borderColor: nodeBorderColor, borderWidth: nodeBorderWidth, borderStyle: 'solid' });
        element.applyBackground({ backgroundColor: nodeBgColor });

        const resolvedContent = this._resolveNodeContent(node);

        if (resolvedContent !== undefined) {
            element.setContent(resolvedContent);
        }

        this._elementToMindNodeMap.set(element, node);

        element.on(ElementEvent.DISPOSE, () => {

            const disposedNode = this._elementToMindNodeMap.get(element);
            if (disposedNode) {
                const disposedOriginalData = this._getOriginalDataForNode(disposedNode);
                this.emit(MindMapEvents.NODE_DESTROYED, {
                    node: disposedOriginalData,
                } as MindMapNodeDestroyEvent);

                // 注意：在此处从 this._elementToMindNodeMap 和 this._mindNodeToOriginalDataMap 中删除条目是可选的。
                // 因为 _layoutAndCreateElementsAndRender 方法在之后会调用 .clear() 来清空这些映射。
                // 如果在这里删除，可以更早地释放引用，但如果 _layoutAndCreateElementsAndRender 的逻辑发生变化，
                // 可能会导致问题。当前保留 _layoutAndCreateElementsAndRender 中的 .clear() 是更稳妥的做法。
                // this._elementToMindNodeMap.delete(element);
                // this._mindNodeToOriginalDataMap.delete(disposedNode);
                // if (disposedNode.id) {
                //     this._linesMap.delete(disposedNode.id); // 清理连接到此节点的线
                // }
            }
        }); // 监听元素销毁事件，通知调用方清理相关被占用资源

        return element;
    }

    /**
     * 解析节点内容。
     * 此方法会决定最终用于节点显示的内容。
     * 如果原始数据 nodeData.content 提供了内容定义，并且与 mindNode.content 中缓存的定义不同，
     * 则会使用 nodeData.content 更新 mindNode.content。
     * @param mindNode - 思维导图节点
     * @returns 解析后的节点显示内容 (字符串、HTML元素或其数组)
     */
    private _resolveNodeContent(mindNode: MindNode): string | HTMLElement | HTMLElement[] | undefined {
        const nodeData = this._getOriginalDataForNode(mindNode); // 获取原始 TreeNode, FlatNode 或 MindNode 自身
        let contentDefinitionToUse: ContentType | undefined = undefined;
        let resolvedDisplayContent: string | HTMLElement | HTMLElement[] | undefined = undefined;

        // 步骤 1: 确定内容定义来源，并酌情更新 mindNode.content
        // 如果 nodeData 是原始的 TreeNode 或 FlatNode (即不是 mindNode 自身)
        if (nodeData !== mindNode) {
            const originalNodeContent = nodeData.content; // 获取原始数据上的 content

            // 检查原始数据上是否有有效的内容定义 (值或函数)
            if (
                typeof originalNodeContent === 'string' ||
                originalNodeContent instanceof HTMLElement ||
                Array.isArray(originalNodeContent) ||
                typeof originalNodeContent === 'function'
            ) {
                // 如果原始数据的内容与 mindNode 缓存的内容不同，则更新 mindNode 的缓存
                if (mindNode.content !== originalNodeContent) {
                    mindNode.content = originalNodeContent as ContentType;
                }
                // 使用 (可能已更新的) mindNode.content 作为内容定义
                contentDefinitionToUse = mindNode.content;
            } else {
                // 原始数据上没有有效的 content 定义，则依赖 mindNode 自身已有的 content
                // (这可能是 undefined，或者是在 MindNode 直接初始化时设置的)
                contentDefinitionToUse = mindNode.content;
            }
        } else {
            // 如果 nodeData 就是 mindNode 自身 (例如，MindMap 直接用 MindNode 实例初始化)
            // 则直接使用 mindNode.content
            contentDefinitionToUse = mindNode.content;
        }

        // 步骤 2: 如果找到了内容定义 (contentDefinitionToUse)，则解析它
        if (contentDefinitionToUse !== undefined) {
            if (typeof contentDefinitionToUse === 'function') {
                // 如果定义是函数，则执行它以获取最终显示内容
                // nodeData 是原始 TreeNode/FlatNode 或 MindNode 实例，符合函数期望
                const callableFunction = contentDefinitionToUse as (data: NodeLike) => string | HTMLElement | HTMLElement[];
                try {
                    resolvedDisplayContent = callableFunction(nodeData);
                } catch (e) {
                    console.error("Error calling node content function:", e);
                    // Potentially handle the error, e.g., by falling back to other content or undefined
                }
            } else {
                // 如果定义是具体值，直接使用
                resolvedDisplayContent = contentDefinitionToUse as unknown as string | HTMLElement | HTMLElement[];
            }
        }

        // 步骤 3: 如果通过上述步骤仍未解析到内容，
        // 则尝试使用 MindMap 构造时传入的全局 _nodeContentCallback
        if (resolvedDisplayContent === undefined && this._nodeContentCallback) {
            resolvedDisplayContent = this._nodeContentCallback(nodeData);
            // 注意: 全局回调的结果不会被缓存回 mindNode.content，
            // 因为 mindNode.content 旨在存储节点自身的特定内容定义。
        }

        return resolvedDisplayContent;
    }

    /**
     * 创建连接线
     * @param from - 起始节点
     * @param to - 结束节点
     * @param isHorizontal - 是否水平连接
     * @returns 连接线对象
     */
    private _createConnectionLine(
        from: MindNode,
        to: MindNode,
        isHorizontal: boolean
    ): Line {
        // 计算连接点而不是直接使用节点位置
        const [fromPoint, toPoint] = this._calculateConnectionPoint(from, to, isHorizontal);
        const { lineWidth, lineColor } = this._style;

        const lineOptions = {
            isHorizontal,
            lineWidth,
            lineColor,
        }

        switch (this._lineShape) {
            case 'curved':
                return new BentLine(fromPoint, toPoint, { isCurved: true, ...lineOptions });
            case 'zigzag':
                return new BentLine(fromPoint, toPoint, { isCurved: false, ...lineOptions });
            case 'straight':
                return new Line(fromPoint, toPoint, { ...lineOptions });
            default:
                throw new Error('Unsupported line shape');
        }
    }

    /**
     * 基于两个节点的位置和尺寸计算边缘连接点
     * @param from - 起始节点
     * @param to - 结束节点
     * @param isHorizontal - 是否水平连接，否则为垂直连接
     * @returns 起始点和结束点的坐标
     */
    private _calculateConnectionPoint(
        from: MindNode,
        to: MindNode,
        isHorizontal: boolean
    ): [Vector, Vector] {
        // 如果水平连接，则计算起始节点的右边缘中间点，以及结束节点的左边缘中间点
        // 如果垂直连接，则计算起始节点的下边缘中间点，以及结束节点的上边缘中间点
        if (isHorizontal) {
            const fromPoint = new Vector(
                from.position.x + from.size.x,
                from.position.y + from.size.y / 2
            );
            const toPoint = new Vector(
                to.position.x,
                to.position.y + to.size.y / 2
            );
            return [fromPoint, toPoint];
        } else {
            const fromPoint = new Vector(
                from.position.x + from.size.x / 2,
                from.position.y + from.size.y
            );
            const toPoint = new Vector(
                to.position.x + to.size.x / 2,
                to.position.y
            );
            return [fromPoint, toPoint];
        }
    }

    // #endregion 渲染与元素创建

    // #region 位置与连接线更新

    /**
     * 更新所有 Element 的位置以匹配其对应 MindNode 的位置
     */
    private _updateAllElementPositions(): void {
        for (const [element, mindNode] of this._elementToMindNodeMap.entries()) {
            element.moveTo(mindNode.position.x, mindNode.position.y);
        }
    }

    /**
     * 更新所有连接线的位置
     */
    private _updateAllConnectionLines(): void {
        // 遍历所有连接线并更新其位置
        for (const [nodeId, line] of this._linesMap.entries()) {
            const childNode = this.findNodeById(nodeId);
            if (childNode && childNode.parent) {
                const [fromPoint, toPoint] = this._calculateConnectionPoint(
                    childNode.parent,
                    childNode,
                    this._isHorizontal
                );
                line.setPoints(fromPoint, toPoint);
            }
        }
    }

    /**
     * 更新指定节点相关的连接线(到父节点和到子节点)
     * @param node - 需要更新连接线的节点
     */
    private _updateConnectionsForNode(node: MindNode): void {
        // 更新到父节点的连线
        if (node.parent) {
            const lineToParent = this._linesMap.get(node.id); // 通过子节点ID获取线
            if (lineToParent) {
                const [fromPoint, toPoint] = this._calculateConnectionPoint(node.parent, node, this._isHorizontal);
                lineToParent.setPoints(fromPoint, toPoint);
            }
        }
        // 更新到子节点的连线
        node.children.forEach(childNode => {
            const lineToChild = this._linesMap.get(childNode.id); // 通过子节点ID获取线
            if (lineToChild) {
                const [fromPoint, toPoint] = this._calculateConnectionPoint(node, childNode, this._isHorizontal);
                lineToChild.setPoints(fromPoint, toPoint);
            }
        });
    }

    /**
     * 更新容器边界以适应新的内容尺寸
     */
    private _updateContainerBounds(): void {
        const bbox = this._rootContainer.getLocalBoundingBox();
        this._rootContainer.setSize({
            width: bbox.width,
            height: bbox.height,
        });

        // 触发重新渲染
        RenderManager.getInstance().renderContainer(this._outerContainer);
    }

    // #endregion 位置与连接线更新

    // #region 交互管理

    /**
     * 初始化或重新初始化拖拽管理器
     * 配置节点和画布的拖拽行为并绑定相关事件
     */
    private _initializeDraggableManager(): void {
        this._draggableManager?.dispose(); // 清理旧的管理器

        const dmDragNodeOption = this._dragOptions.enableNodeDrag
            ? (this._dragOptions.dragNodeButton || DEFAULT_DRAG_NODE_BUTTON)
            : false;
        const dmDragAllOption = this._dragOptions.enableCanvasDrag
            ? (this._dragOptions.dragCanvasButton || DEFAULT_DRAG_CANVAS_BUTTON)
            : false;

        this._draggableManager = new DraggableManager(this._outerContainer.target, {
            dragNode: dmDragNodeOption,
            dragAll: dmDragAllOption,
            useThrottle: this._dragOptions.useThrottle,
            throttleWait: this._dragOptions.throttleWait,
        });

        // 从 elementToMindNodeMap 获取可拖拽的 Element 实例
        const draggableElements: Element[] = Array.from(this._elementToMindNodeMap.keys());
        this._draggableManager.addBatch(draggableElements);

        // 监听节点拖拽事件
        this._draggableManager.on(DragEvents.NODE, (e: DragEventData) => {
            const { node: draggedElement, deltaX, deltaY } = e;
            // 确保 draggedElement 是 Element 实例，并且 deltaX/Y 有效
            if (draggedElement instanceof Element && deltaX !== undefined && deltaY !== undefined) {
                this._handleNodeDrag(draggedElement, deltaX, deltaY);
            }
        });

        // 监听画布拖拽开始事件
        this._draggableManager.on(DragEvents.CANVAS_START, (e: DragEventData) => {
            const { originalEvent, button, mouseX, mouseY } = e;
            this.emit(MindMapEvents.CANVAS_DRAG_START, {
                originalEvent,
                button,
                mouseX,
                mouseY
            } as MindMapCanvasEvent);
        });

        // 监听画布拖拽事件
        this._draggableManager.on(DragEvents.CANVAS, (e: DragEventData) => {
            const { originalEvent, button, mouseX, mouseY, deltaX, deltaY } = e;
            if (deltaX !== undefined && deltaY !== undefined) {
                this._handleCanvasDrag(deltaX, deltaY);

                // 触发画布拖拽事件
                this.emit(MindMapEvents.CANVAS_DRAG, {
                    originalEvent,
                    button,
                    mouseX,
                    mouseY,
                    deltaX,
                    deltaY
                } as MindMapCanvasEvent);
            }
        });

        // 监听画布拖拽结束事件
        this._draggableManager.on(DragEvents.CANVAS_END, (e: DragEventData) => {
            const { originalEvent, button, mouseX, mouseY } = e;
            this.emit(MindMapEvents.CANVAS_DRAG_END, {
                originalEvent,
                button,
                mouseX,
                mouseY
            } as MindMapCanvasEvent);
        });
    }

    /**
     * 初始化鼠标滚轮管理器
     * 配置滚轮缩放行为并绑定相关事件
     */
    private _initializeWheelManager(): void {
        if (this._wheelManager) {
            this._wheelManager.dispose(); // 清理旧的管理器
        }

        if (!this._zoomOptions.enableWheelZoom) {
            return; // 如果不启用滚轮缩放，则不初始化
        }

        this._wheelManager = new WheelManager(this._outerContainer.target, {
            useThrottle: this._zoomOptions.useThrottle,
            throttleWait: this._zoomOptions.throttleWait,
            preventDefault: true
        });

        // 监听滚轮事件
        this._wheelManager.on(WheelEvents.SCROLL, (data: WheelEventData) => {
            const { deltaY, mouseX, mouseY, originalEvent } = data;

            if (deltaY === 0) return; // 如果没有垂直滚动则忽略

            // 根据 invertWheelDirection 配置决定滚动方向
            const isZoomIn = this._zoomOptions.invertWheelDirection ? deltaY > 0 : deltaY < 0;

            // 根据当前缩放比例检查是否可以继续缩放
            const currentScale = this._rootContainer.scaling;

            if (isZoomIn && currentScale.x >= this._zoomOptions.maxZoom) return;
            if (!isZoomIn && currentScale.x <= this._zoomOptions.minZoom) return;

            if (isZoomIn) {
                this._zoomIn(mouseX, mouseY, originalEvent);
            } else {
                this._zoomOut(mouseX, mouseY, originalEvent);
            }
        });
    }

    /**
     * 设置容器尺寸变化监听器
     */
    private _setupContainerResizeListener(): void {
        this._outerContainer.on(ContainerEvents.RESIZE, (resizeData) => {
            const { width, height } = resizeData;

            // 触发 MindMap 的容器尺寸变化事件
            this.emit(MindMapEvents.CONTAINER_RESIZE, {
                width,
                height,
                // 如果需要旧尺寸信息，可以在这里添加
            } as MindMapContainerResizeEvent);

            // 可选：当容器尺寸变化时，你可能需要重新调整布局或其他操作
            // this._handleContainerResize(width, height);
        });
    }

    /**
     * 处理节点拖拽的逻辑
     * @param draggedElement - 被拖拽的 DOM 元素对应的 Element 对象
     * @param deltaX - X 轴拖拽距离
     * @param deltaY - Y 轴拖拽距离
     */
    private _handleNodeDrag(draggedElement: Element, deltaX: number, deltaY: number): void {
        if (!draggedElement.id) return;

        const draggedMindNode = this._elementToMindNodeMap.get(draggedElement);

        if (!draggedMindNode) return;

        // 1. 更新 MindNode 的逻辑位置
        draggedMindNode.position.x += deltaX;
        draggedMindNode.position.y += deltaY;

        // 2. 移动 Element 的视觉位置
        draggedElement.move(deltaX, deltaY);

        // 3. 更新连接线
        this._updateConnectionsForNode(draggedMindNode);
    }

    /**
     * 处理画布拖拽的逻辑
     * @param deltaX - X 轴拖拽距离
     * @param deltaY - Y 轴拖拽距离
     */
    private _handleCanvasDrag(deltaX: number, deltaY: number): void {
        this._rootContainer.move(deltaX, deltaY);
    }

    // #endregion 交互管理

    // #region 调试工具

    /**
     * 输出思维导图容器的调试信息
     */
    debug() {
        const { width, height } = this._outerContainer;
        console.log(`Outer container size: ${width} x ${height}`);

        const bbox = this._rootContainer.getBoundingBox();
        console.log(`Root container size: ${bbox.width} x ${bbox.height}`);
    }

    // #endregion 调试工具

    // #region 全屏模式管理

    /**
     * 进入全屏模式
     * @param mode - 全屏模式 ('native' 或 'pseudo')。如果未提供，则使用构造函数中配置的默认模式
     * @returns Promise，成功时 resolve true，失败时 reject error
     */
    async enterFullscreen(mode?: 'native' | 'pseudo'): Promise<boolean> {
        const targetMode = mode || this._fullscreenOptions.fullscreenMode;

        // 如果已经是目标模式的全屏状态，则直接返回
        if (
            (targetMode === 'native' && this.isNativeFullscreen) ||
            (targetMode === 'pseudo' && this.isPseudoFullscreen)
        ) {
            return true;
        }

        // 如果当前处于另一种全屏模式，先退出
        if (this.isFullscreen) {
            await this.exitFullscreen();
        }

        if (targetMode === 'native') {
            return this._enterNativeFullscreen();
        } else {
            this._enterPseudoFullscreen();
            return true; // 模拟全屏是同步的
        }
    }

    /**
     * 退出全屏模式
     * 会自动检测并退出当前激活的全屏模式 (原生或模拟)
     * @returns Promise，成功时 resolve true，失败时 reject error
     */
    async exitFullscreen(): Promise<boolean> {
        if (this.isNativeFullscreen) {
            return await this._exitNativeFullscreen();
        }
        if (this.isPseudoFullscreen) {
            this._exitPseudoFullscreen();
            return true; // 模拟全屏是同步的
        }
        return true; // 已经不在全屏状态
    }

    /**
     * 切换全屏状态
     * @param mode - 全屏模式 ('native' 或 'pseudo')。如果未提供，则使用构造函数中配置的默认模式
     * @returns Promise，成功时 resolve 新的全屏状态 (true 为进入，false 为退出)
     */
    async toggleFullscreen(mode?: 'native' | 'pseudo'): Promise<boolean> {
        const targetMode = mode || this._fullscreenOptions.fullscreenMode;

        // 检查当前全屏状态
        // 注意：这里的 isFullscreen 是一个计算属性，包含了原生和模拟全屏状态
        const isCurrentlyFullscreen = this.isFullscreen;
        // 检查当前是否处于目标模式的全屏状态
        // 注意：这里的 isNativeFullscreen 和 isPseudoFullscreen 是计算属性，分别表示原生和模拟全屏状态
        const isCurrentlyInTargetMode =
            (targetMode === 'native' && this.isNativeFullscreen) ||
            (targetMode === 'pseudo' && this.isPseudoFullscreen);

        if (isCurrentlyFullscreen) {
            // 如果当前是全屏状态，则统一退出
            await this.exitFullscreen();
            // 如果之前不是目标模式，则退出后立即进入目标模式 (实现模式切换)
            if (!isCurrentlyInTargetMode) {
                await this.enterFullscreen(targetMode);
                return true;
            }
            // 如果之前就是目标模式，则只退出
            return false;
        } else {
            // 如果当前不是全屏状态，则直接进入目标模式
            await this.enterFullscreen(targetMode);
            return true;
        }
    }

    /**
     * 进入原生全屏模式
     * @private
     */
    private async _enterNativeFullscreen(): Promise<boolean> {
        if (this._fullscreenTransitioning || this.isNativeFullscreen) {
            return this.isNativeFullscreen;
        }
        this._fullscreenTransitioning = true;

        try {
            const element = this._outerContainer.target.htmlEle;

            if (!document.fullscreenElement) {
                // 在请求全屏之前应用背景色，以避免闪烁
                this._outerContainer.applyFullscreenBackground(this._fullscreenOptions.fullscreenBgColor);
                await element.requestFullscreen();
                return true;
            }
            this._fullscreenTransitioning = false; // 如果已处于全屏，则重置状态
            return false; // 已经处于全屏状态
        } catch (error) {
            console.error('[MindMap] 进入原生全屏失败:', error);
            this._fullscreenTransitioning = false; // 出错时重置状态
            throw error;
        }
    }

    /**
     * 退出原生全屏模式
     * @private
     */
    private async _exitNativeFullscreen(): Promise<boolean> {
        if (this._fullscreenTransitioning || !this.isNativeFullscreen) {
            return !this.isNativeFullscreen;
        }
        this._fullscreenTransitioning = true;

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
                return true;
            }
            this._fullscreenTransitioning = false; // 如果已退出全屏，则重置状态
            return false; // 不在全屏状态
        } catch (error) {
            console.error('[MindMap] 退出全屏失败:', error);
            this._fullscreenTransitioning = false; // 出错时重置状态
            throw error;
        }
    }

    /**
     * 初始化原生全屏状态监听器
     * @private
     */
    private _initializeFullscreenListener(): void {
        this._fullscreenChangeListener = () => {
            const isFullscreen = document.fullscreenElement === this._outerContainer.target.htmlEle;

            // 状态切换完成后，重置过渡标记
            this._fullscreenTransitioning = false;

            if (this._isNativeFullscreen !== isFullscreen) {
                this._isNativeFullscreen = isFullscreen;

                // 当进入/退出全屏时，可能需要重新调整容器尺寸
                if (isFullscreen) {
                    // 进入全屏时，容器会自动填满整个屏幕
                    // 注意：此处保留调用是为了处理非API触发的全屏（如F11）
                    // 如果是通过 enterFullscreen() 触发的，这次调用是冗余的但无害
                    this._outerContainer.applyFullscreenBackground(this._fullscreenOptions.fullscreenBgColor);
                    // 这里可以添加一些全屏特有的样式或行为
                } else {
                    // 退出全屏时恢复原有样式
                    this._outerContainer.restoreOriginalBackground();
                    // 这里可以添加一些退出全屏的特有样式或行为
                }

                // 触发全屏状态变化事件
                this.emit(MindMapEvents.FULLSCREEN_CHANGE, {
                    isFullscreen,
                    element: this._outerContainer.target.htmlEle
                } as MindMapFullscreenEvent);
            }
        };

        document.addEventListener('fullscreenchange', this._fullscreenChangeListener);
    }

    /**
     * 进入模拟全屏模式
     * @private
     */
    private _enterPseudoFullscreen(): void {
        if (this.isPseudoFullscreen) return;

        const element = this._outerContainer.target.htmlEle;

        this._originalBodyStyle = {
            overflow: document.body.style.overflow,
        };

        // 应用全屏样式
        this._outerContainer.applyPseudoFullscreenStyles(this._fullscreenOptions.fullscreenBgColor);
        document.body.style.overflow = 'hidden'; // 隐藏滚动条

        this._isPseudoFullscreen = true;

        // 触发事件
        this.emit(MindMapEvents.FULLSCREEN_CHANGE, {
            isFullscreen: true,
            element
        } as MindMapFullscreenEvent);
    }

    /**
     * 退出模拟全屏模式
     * @private
     */
    private _exitPseudoFullscreen(): void {
        if (!this.isPseudoFullscreen) return;

        const element = this._outerContainer.target.htmlEle;

        // 恢复原始样式
        this._outerContainer.restoreOriginalStyles();
        // 恢复 body 样式
        if (this._originalBodyStyle) {
            document.body.style.overflow = this._originalBodyStyle.overflow;
        }

        this._isPseudoFullscreen = false;
        this._originalBodyStyle = undefined;

        // 触发事件
        this.emit(MindMapEvents.FULLSCREEN_CHANGE, {
            isFullscreen: false,
            element
        } as MindMapFullscreenEvent);
    }

    // #endregion 全屏模式管理
}

/**
 * 思维导图树形数据类型(用于描述思维导图节点的结构和内容)
 * 通过 children 数组直接表示层级关系
 */
export interface TreeNode {
    /** 节点唯一标识符 */
    id?: string;
    /** 节点位置坐标，可以是对象或 Vector 实例 */
    position?: { x: number, y: number } | Vector;
    /** 节点尺寸，可以是对象或 Vector 实例 */
    size?: { x: number, y: number } | Vector;
    /** 子节点数组 */
    children?: TreeNode[];
    /** 节点是否折叠，默认为 false (不折叠) */
    collapsed?: boolean;
    /**
     * 节点内容，可以是文本、HTML 元素、HTML 元素数组，或一个返回文本/HTML元素/HTML元素数组的回调函数
     * 如果是函数，它将接收原始的 TreeNode 数据作为参数
     * 优先级比传入 MindMap 的 contentCallback 高
     */
    content?: ContentType<TreeNode>;
    /** 允许任意额外属性 */
    [key: string]: any;
}

/**
 * 思维导图扁平数据类型(用于描述思维导图节点的结构和内容)
 * 通过 parentId 间接表示层级关系
 */
export interface FlatNode {
    /** 节点唯一标识符 */
    id?: string;
    /** 父节点ID */
    parentId?: string;
    /** 节点位置坐标，可以是对象或 Vector 实例 */
    position?: { x: number, y: number } | Vector;
    /** 节点尺寸，可以是对象或 Vector 实例 */
    size?: { x: number, y: number } | Vector;
    /** 节点是否折叠，默认为 false (不折叠) */
    collapsed?: boolean;
    /**
     * 节点内容，可以是文本、HTML 元素、HTML 元素数组，或一个返回文本/HTML元素/HTML元素数组的回调函数
     * 如果是函数，它将接收原始的 FlatNode 数据作为参数
     * 优先级比传入 MindMap 的 contentCallback 高
     */
    content?: ContentType<FlatNode>;
    /** 允许任意额外属性 */
    [key: string]: any;
}

/**
 * 思维导图样式配置类型
 * 用于定制思维导图的视觉外观
 */
export type MindMapStyle = {
    /** 连接线宽度 */
    lineWidth?: number;
    /** 连接线颜色 */
    lineColor?: string;
    /** 节点边框宽度 */
    nodeBorderWidth?: number;
    /** 节点背景颜色 */
    nodeBgColor?: string;
    /** 节点边框颜色 */
    nodeBorderColor?: string;
}

/**
 * 思维导图对齐选项类型
 * 控制思维导图节点的对齐方式
 */
export type MindMapAlign = {
    /** 
     * 对齐模式
     * - cross-axis-start-edge: 交叉轴起始边与视口起始边对齐
     * - cross-axis-center: 交叉轴居中对齐到视口中心
     * - node-center: 节点中心居中对齐到视口中心
     */
    mode: AlignOptions['mode'];
    /** 目标节点ID，仅用于 'node-center' 模式 */
    targetNodeId?: AlignOptions['targetNodeId'];
}

/**
 * 思维导图拖拽配置类型
 * 定制思维导图的拖拽行为
 */
export type MindMapDragOptions = {
    /** 是否启用节点拖拽 */
    enableNodeDrag?: boolean;
    /** 是否启用画布拖拽 */
    enableCanvasDrag?: boolean;
    /** 拖拽节点的鼠标按钮 */
    dragNodeButton?: MouseButton[] | MouseButton;
    /** 拖拽画布的鼠标按钮 */
    dragCanvasButton?: MouseButton[] | MouseButton;
    /** 是否使用节流减少事件触发频率 */
    useThrottle?: boolean;
    /** 节流函数的等待时间 */
    throttleWait?: number;
}

/**
 * 思维导图缩放配置类型
 * 定制思维导图的缩放行为
 */
export type MindMapZoomOptions = {
    /** 是否启用滚轮缩放 */
    enableWheelZoom?: boolean;
    /** 每次缩放步长，如 0.1 表示 10% */
    zoomStep?: number;
    /** 是否反转滚轮方向 */
    invertWheelDirection?: boolean;
    /** 最小缩放比例，如 0.1 表示最小缩放到 10% */
    minZoom?: number;
    /** 最大缩放比例，如 5.0 表示最大缩放到 500% */
    maxZoom?: number;
    /** 是否使用节流减少事件触发频率 */
    useThrottle?: boolean;
    /** 节流函数的等待时间 */
    throttleWait?: number;
}

export type FullscreenOptions = {
    /** 全屏时背景颜色(可以设置为任何有效的 CSS 颜色值) */
    fullscreenBgColor?: string;
    /** 
     * 全屏模式
     * - 'native': 使用浏览器原生全屏 API
     * - 'pseudo': 使用 CSS 模拟全屏效果
     * @default 'native'
     */
    fullscreenMode?: 'native' | 'pseudo';
}

/**
 * 思维导图调试选项类型
 * 不对外公开，仅用于开发和调试
 */
export type DebugOptions = {
    rootContainerBgColor?: string; // 根容器背景颜色
    drawGridBackground?: boolean; // 是否绘制网格背景
}

const DEFAULT_DRAG_NODE_BUTTON = MouseButton.Left; // 默认拖拽节点使用左键
const DEFAULT_DRAG_CANVAS_BUTTON = [MouseButton.Right, MouseButton.Middle]; // 默认拖拽画布使用右键

/**
 * 思维导图全屏选项的默认配置
 */
export const DEFAULT_FULLSCREEN_OPTIONS: Required<FullscreenOptions> = {
    fullscreenBgColor: '#FFFFFF',
    fullscreenMode: 'native',
};

/**
 * 思维导图拖拽选项的默认配置
 */
export const DEFAULT_DRAG_OPTIONS: Required<MindMapDragOptions> = {
    enableNodeDrag: true,
    enableCanvasDrag: true,
    dragNodeButton: DEFAULT_DRAG_NODE_BUTTON,
    dragCanvasButton: DEFAULT_DRAG_CANVAS_BUTTON,
    useThrottle: true,
    throttleWait: 16, // 默认节流等待时间
};

/**
 * 思维导图缩放选项的默认配置
 */
export const DEFAULT_ZOOM_OPTIONS: Required<MindMapZoomOptions> = {
    enableWheelZoom: true,
    zoomStep: 0.1,
    invertWheelDirection: false,
    minZoom: 0.1,
    maxZoom: 5.0,
    useThrottle: true,   // 默认启用节流
    throttleWait: 16     // 默认节流等待时间
};

export type NodeLike = MindNode | TreeNode | FlatNode;

/**
 * MindMap 节点拖拽事件数据
 */
export interface MindMapNodeEvent {
    /** 原始鼠标事件 */
    originalEvent: MouseEvent;
    /** 按下的鼠标按钮 */
    button: MouseButton | null;
    /** 被拖拽的 MindNode 节点 */
    node: MindNode;
    /** 拖拽开始时鼠标在容器内的 X 坐标 */
    mouseX: number;
    /** 拖拽开始时鼠标在容器内的 Y 坐标 */
    mouseY: number;
    /** X 轴拖拽距离 (仅 NODE_DRAG 事件) */
    deltaX?: number;
    /** Y 轴拖拽距离 (仅 NODE_DRAG 事件) */
    deltaY?: number;
}

/**
 * MindMap 容器尺寸变化事件数据
 */
export interface MindMapContainerResizeEvent {
    /** 新的容器宽度 */
    width: number;
    /** 新的容器高度 */
    height: number;
    // /** 旧的容器宽度 */
    // oldWidth?: number;
    // /** 旧的容器高度 */
    // oldHeight?: number;
}

/**
 * MindMap 画布拖拽事件数据
 */
export interface MindMapCanvasEvent {
    /** 原始鼠标事件 */
    originalEvent: MouseEvent;
    /** 按下的鼠标按钮 */
    button: MouseButton | null;
    /** 拖拽开始时鼠标在容器内的 X 坐标 */
    mouseX: number;
    /** 拖拽开始时鼠标在容器内的 Y 坐标 */
    mouseY: number;
    /** X 轴拖拽距离 (仅 CANVAS_DRAG 事件) */
    deltaX?: number;
    /** Y 轴拖拽距离 (仅 CANVAS_DRAG 事件) */
    deltaY?: number;
}

/**
 * MindMap 缩放事件数据
 */
export interface MindMapZoomEvent {
    /** 原始滚轮事件 (可选) */
    originalEvent?: WheelEvent;
    /** 当前的缩放比例向量 {x, y} */
    scale: Vector;
    /** 缩放中心在容器内的 X 坐标 */
    mouseX: number;
    /** 缩放中心在容器内的 Y 坐标 */
    mouseY: number;
}

/**
 * MindMap 布局更新事件数据
 * 当思维导图的布局（例如，从水平切换到垂直）发生变化并更新完成后触发
 */
export interface MindMapLayoutEvent {
    direction: 'horizontal' | 'vertical'; // 布局方向
}

/**
 * MindMap 节点销毁事件数据
 */
export interface MindMapNodeDestroyEvent {
    /** 被销毁节点对应的原始数据 */
    node: NodeLike;
}

/**
 * MindMap 节点折叠事件数据
 */
export interface MindMapNodeCollapseEvent {
    /** 折叠状态发生变化的节点 ID */
    nodeId: string;
    /** 当前折叠状态 */
    collapsed: boolean;
    /** 被折叠/展开的节点对应的原始数据 */
    node: NodeLike;
}

/**
 * 全屏事件数据
 */
export interface MindMapFullscreenEvent {
    /** 是否进入全屏状态 */
    isFullscreen: boolean;
    /** 全屏容器元素 */
    element: HTMLElement;
}

/**
 * 思维导图事件枚举
 */
export enum MindMapEvents {
    // /** 节点开始拖拽 */
    // NODE_DRAG_START = 'node:dragstart',
    // /** 节点拖拽中 */
    // NODE_DRAG = 'node:drag',
    // /** 节点结束拖拽 */
    // NODE_DRAG_END = 'node:dragend',

    /** 画布开始拖拽事件，抛出 {@link MindMapCanvasEvent} 类型事件对象 */
    CANVAS_DRAG_START = 'mindmap:canvas-dragstart',
    /** 画布拖拽中事件，抛出 {@link MindMapCanvasEvent} 类型事件对象 */
    CANVAS_DRAG = 'mindmap:canvas-drag',
    /** 画布结束拖拽事件，抛出 {@link MindMapCanvasEvent} 类型事件对象 */
    CANVAS_DRAG_END = 'mindmap:canvas-dragend',
    /** 缩放变化事件，抛出 {@link MindMapZoomEvent} 类型事件对象 */
    ZOOM_CHANGE = 'mindmap:zoom-change',
    /** 容器尺寸变化事件，抛出 {@link MindMapContainerResizeEvent} 类型事件对象 */
    CONTAINER_RESIZE = 'mindmap:container-resize',
    /** 布局更新完成事件，抛出 {@link MindMapLayoutEvent} 类型事件对象 */
    LAYOUT_UPDATED = 'mindmap:layout-updated',
    /** 节点折叠状态切换事件事件，抛出 {@link MindMapNodeCollapseEvent} 类型事件对象 */
    NODE_COLLAPSE_CHANGED = 'mindmap:node-collapse-changed',
    /** 节点销毁事件，抛出 {@link MindMapNodeDestroyEvent} 类型事件对象 */
    NODE_DESTROYED = 'node:destroyed',
    /** 思维导图销毁事件 */
    MINDMAP_DESTROYED = 'mindmap:destroyed',
    /** 全屏状态变化事件，抛出 {@link MindMapFullscreenEvent} 类型事件对象 */
    FULLSCREEN_CHANGE = 'mindmap:fullscreen-change',
}

/**
 * MindMap 事件名称到事件数据类型的映射
 */
export interface MindMapEventMap {
    // [MindMapEvents.NODE_DRAG_START]: MindMapNodeEvent;
    // [MindMapEvents.NODE_DRAG]: MindMapNodeEvent;
    // [MindMapEvents.NODE_DRAG_END]: MindMapNodeEvent;

    [MindMapEvents.CANVAS_DRAG_START]: MindMapCanvasEvent;
    [MindMapEvents.CANVAS_DRAG]: MindMapCanvasEvent;
    [MindMapEvents.CANVAS_DRAG_END]: MindMapCanvasEvent;
    [MindMapEvents.ZOOM_CHANGE]: MindMapZoomEvent;
    [MindMapEvents.CONTAINER_RESIZE]: MindMapContainerResizeEvent;
    [MindMapEvents.LAYOUT_UPDATED]: MindMapLayoutEvent;
    [MindMapEvents.NODE_COLLAPSE_CHANGED]: MindMapNodeCollapseEvent;
    [MindMapEvents.NODE_DESTROYED]: MindMapNodeDestroyEvent;
    [MindMapEvents.MINDMAP_DESTROYED]: void;
    [MindMapEvents.FULLSCREEN_CHANGE]: MindMapFullscreenEvent;
}
